import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchCampaigns,
  toggleCampaign,
  purgeCampaign,
  createCampaign,
  fetchSeedAccounts,
  batchDeleteSeeds,
  fetchSeedRatioStats,
  type SeedCampaign,
  type SeedRatioStats,
} from '../../lib/seedService';
import { fetchAuditLog, type AuditEntry } from '../../lib/notificationService';
import { logger } from '../../lib/logger';

// ─── Ratio Bar ──────────────────────────────────────────────

function RatioBar({ label, seed, real }: { label: string; seed: number; real: number }) {
  const total = seed + real;
  const seedPct = total > 0 ? Math.round((seed / total) * 100) : 0;
  const realPct = total > 0 ? 100 - seedPct : 0;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-500">{seed} seeds · {real} réels ({total})</span>
      </div>
      <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
        {realPct > 0 && (
          <div className="bg-emerald-500 h-full transition-all" style={{ width: `${realPct}%` }} />
        )}
        {seedPct > 0 && (
          <div className="bg-indigo-400 h-full transition-all" style={{ width: `${seedPct}%` }} />
        )}
      </div>
      <div className="flex items-center justify-between text-[10px] text-gray-400">
        <span>{realPct}% réel</span>
        <span>{seedPct}% seed</span>
      </div>
    </div>
  );
}

// ─── Audit Action Labels ────────────────────────────────────

const actionLabels: Record<string, string> = {
  create_campaign: 'Campagne créée',
  archive_campaign: 'Campagne archivée',
  purge_campaign: 'Campagne purgée',
  create_seed_accounts_batch: 'Comptes seeds (batch)',
  create_seed_account_manual: 'Compte seed (manuel)',
  update_seed_account: 'Compte seed modifié',
  delete_seed_account: 'Compte seed supprimé',
  batch_delete_seeds: 'Suppression batch',
  create_seed_videos: 'Vidéos seed créées',
  create_seed_offer: 'Offre seed créée',
  update_seed_offer: 'Offre seed modifiée',
  delete_seed_offer: 'Offre seed supprimée',
  generate_video_interactions: 'Interactions vidéo',
  generate_swipes: 'Swipes générés',
  generate_favorites: 'Favoris générés',
  generate_seed_applications: 'Candidatures seed',
  boost_real_user: 'Boost utilisateur',
  boost_real_offer: 'Boost offre',
  generate_employer_profile_views: 'Vues profil employeur',
  generate_employer_selections: 'Sélections employeur',
  send_admin_notification: 'Notification admin',
  send_bulk_notification: 'Notifications bulk',
  delete_seed_trace: 'Trace supprimée',
};

// ─── Component ──────────────────────────────────────────────

const statusColors: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-gray-100 text-gray-500 border-gray-200',
  purged: 'bg-red-50 text-red-600 border-red-200',
};

const statusLabels: Record<string, string> = {
  active: 'Active',
  archived: 'Archivée',
  purged: 'Purgée',
};

const CBDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<SeedCampaign[]>([]);
  const [ratioStats, setRatioStats] = useState<SeedRatioStats | null>(null);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState<SeedCampaign | null>(null);
  const [purgeInput, setPurgeInput] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showCleanup, setShowCleanup] = useState(false);
  const [cleanupAccounts, setCleanupAccounts] = useState<{ userId: string; name: string; seedTag: string }[]>([]);
  const [cleanupSelected, setCleanupSelected] = useState<Set<string>>(new Set());
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [tab, setTab] = useState<'campaigns' | 'audit'>('campaigns');

  const load = async () => {
    setLoading(true);
    try {
      const [c, r, a] = await Promise.all([
        fetchCampaigns(),
        fetchSeedRatioStats(),
        fetchAuditLog(30),
      ]);
      setCampaigns(c);
      setRatioStats(r);
      setAuditLog(a);
    } catch (err) {
      logger.error('load_dashboard', 'CBDashboard', { error: String(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const c = await createCampaign(newName.trim(), newDesc.trim());
      setCampaigns((prev) => [c, ...prev]);
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (c: SeedCampaign) => {
    if (!confirm(`Désactiver la campagne « ${c.name} » ?\n\nComptes gelés, offres fermées, vidéos retirées du feed.\nRéversible à tout moment via « Réactiver ».`)) return;
    setActionLoading(c.id);
    try {
      const r = await toggleCampaign(c.name, 'deactivate');
      setCampaigns((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: 'archived' } : x)));
      alert(`Campagne désactivée : ${r.accounts} compte(s), ${r.offers} offre(s), ${r.videos} vidéo(s).`);
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (c: SeedCampaign) => {
    setActionLoading(c.id);
    try {
      const r = await toggleCampaign(c.name, 'reactivate');
      setCampaigns((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: 'active' } : x)));
      alert(`Campagne réactivée : ${r.accounts} compte(s), ${r.offers} offre(s), ${r.videos} vidéo(s).`);
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePurge = async () => {
    if (!purgeConfirm || purgeInput !== purgeConfirm.name) return;
    setActionLoading(purgeConfirm.id);
    try {
      await purgeCampaign(purgeConfirm.name);
      setCampaigns((prev) => prev.map((x) => (x.id === purgeConfirm.id ? { ...x, status: 'purged', accountsCount: 0, offersCount: 0, interactionsCount: 0 } : x)));
      setPurgeConfirm(null);
      setPurgeInput('');
      load();
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const openCleanup = async () => {
    setShowCleanup(true);
    setCleanupLoading(true);
    try {
      const accs = await fetchSeedAccounts();
      setCleanupAccounts(accs.map((a) => ({
        userId: a.userId,
        name: `${a.firstName} ${a.lastName}`,
        seedTag: a.seedTag,
      })));
    } catch (err) {
      logger.error('load_cleanup_accounts', 'CBDashboard', { error: String(err) });
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCleanupDelete = async () => {
    if (!confirm(`Supprimer ${cleanupSelected.size} comptes seeds et toutes leurs traces ?`)) return;
    setCleanupLoading(true);
    try {
      await batchDeleteSeeds(Array.from(cleanupSelected));
      setCleanupSelected(new Set());
      setShowCleanup(false);
      load();
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setCleanupLoading(false);
    }
  };

  const totalAccounts = campaigns.reduce((s, c) => s + c.accountsCount, 0);
  const totalOffers = campaigns.reduce((s, c) => s + c.offersCount, 0);
  const totalInteractions = campaigns.reduce((s, c) => s + c.interactionsCount, 0);
  const activeCampaigns = campaigns.filter((c) => c.status === 'active').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Campagnes actives', value: activeCampaigns, color: 'indigo' },
          { label: 'Comptes seeds', value: totalAccounts, color: 'violet' },
          { label: 'Offres seeds', value: totalOffers, color: 'purple' },
          { label: 'Interactions seeds', value: totalInteractions, color: 'fuchsia' },
        ].map((kpi) => (
          <div key={kpi.label} className={`bg-white rounded-xl border border-${kpi.color}-200 px-5 py-4`}>
            <p className={`text-2xl font-bold text-${kpi.color}-600`}>{kpi.value}</p>
            <p className="text-xs text-gray-500 mt-1">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Ratio Gauges */}
      {ratioStats && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Ratio Seeds / Réels</h2>
            <button
              onClick={openCleanup}
              className="px-4 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
            >
              Nettoyage progressif
            </button>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <RatioBar label="Comptes" seed={ratioStats.accounts.seed} real={ratioStats.accounts.real} />
            <RatioBar label="Offres" seed={ratioStats.offers.seed} real={ratioStats.offers.real} />
            <RatioBar label="Vidéos" seed={ratioStats.videos.seed} real={ratioStats.videos.real} />
            <RatioBar label="Interactions" seed={ratioStats.interactions.seed} real={ratioStats.interactions.real} />
            <RatioBar label="Candidatures" seed={ratioStats.applications.seed} real={ratioStats.applications.real} />
          </div>
          <p className="text-[10px] text-gray-400 text-center">
            Objectif : arriver à 100% réel. Utilisez le nettoyage progressif pour supprimer les seeds graduellement.
          </p>
        </div>
      )}

      {/* Onglet « Journal d'audit » masqué (simplification UI 14/07 : contenu
          incompréhensible en l'état). Le rendu et le fetch sont conservés —
          restaurer la barre d'onglets ci-dessous pour le réactiver. */}
      {false && (
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          <button onClick={() => setTab('campaigns')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === 'campaigns' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500'}`}>
            Campagnes
          </button>
          <button onClick={() => setTab('audit')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === 'audit' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500'}`}>
            Journal d'audit
          </button>
        </div>
      )}

      {tab === 'campaigns' && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Campagnes de seeding</h2>
            <button onClick={() => setShowCreate(true)} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors">
              + Nouvelle campagne
            </button>
          </div>

          <div className="space-y-3">
            {campaigns.length === 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
                Aucune campagne. Créez votre première campagne de seeding.
              </div>
            )}
            {campaigns.map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-6 hover:border-indigo-300 transition-colors">
                {/* Zone cliquable → fiche campagne (stats, comptes, annonces, actions) */}
                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => navigate(`/command-board/campaign/${encodeURIComponent(c.name)}`)}
                  title="Ouvrir la fiche campagne"
                >
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-semibold text-gray-900 hover:text-indigo-600 transition-colors">{c.name}</h3>
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full border ${statusColors[c.status]}`}>
                      {statusLabels[c.status]}
                    </span>
                  </div>
                  {c.description && <p className="text-sm text-gray-500 truncate">{c.description}</p>}
                  <p className="text-xs text-gray-400 mt-1">Créée le {c.createdAt}</p>
                </div>

                <div className="flex items-center gap-6 text-center">
                  <div>
                    <p className="text-lg font-bold text-indigo-600">{c.accountsCount}</p>
                    <p className="text-[10px] text-gray-400 uppercase">Comptes</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-purple-600">{c.offersCount}</p>
                    <p className="text-[10px] text-gray-400 uppercase">Offres</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-fuchsia-600">{c.interactionsCount}</p>
                    <p className="text-[10px] text-gray-400 uppercase">Interactions</p>
                  </div>
                </div>

                {c.status === 'active' && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate(`/command-board/accounts?campaign=${encodeURIComponent(c.name)}`)} className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors">
                      Comptes
                    </button>
                    <button onClick={() => handleDeactivate(c)} disabled={actionLoading === c.id} className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-md transition-colors disabled:opacity-40" title="Éteint tout le boost : comptes gelés, offres fermées, vidéos retirées du feed. Réversible.">
                      Désactiver
                    </button>
                    <button onClick={() => { setPurgeConfirm(c); setPurgeInput(''); }} disabled={actionLoading === c.id} className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-40">
                      Purger
                    </button>
                  </div>
                )}
                {c.status === 'archived' && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleReactivate(c)} disabled={actionLoading === c.id} className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors disabled:opacity-40" title="Rallume le boost : comptes réactivés, offres réouvertes, vidéos remises au feed.">
                      Réactiver
                    </button>
                    <button onClick={() => { setPurgeConfirm(c); setPurgeInput(''); }} disabled={actionLoading === c.id} className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-40">
                      Purger
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'audit' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 text-sm">Dernières opérations growth</h3>
          </div>
          {auditLog.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">Aucune opération enregistrée</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Action</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Cible</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Détails</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {auditLog.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-900">
                      {actionLabels[e.action] || e.action}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 font-mono">
                      {e.targetType && `${e.targetType}/${e.targetId?.slice(0, 8) || '—'}`}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[200px] truncate">
                      {e.details?.count && `${e.details.count} éléments`}
                      {e.details?.name && ` · ${e.details.name}`}
                      {e.details?.title && ` · ${e.details.title}`}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">{e.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
              <h3 className="text-lg font-semibold text-gray-900">Nouvelle campagne</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom (identifiant unique)</label>
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value.replace(/\s+/g, '-').toLowerCase())} placeholder="ex: launch-paris-q1" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Objectif de la campagne..." rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 resize-none" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">Annuler</button>
              <button onClick={handleCreate} disabled={!newName.trim() || creating} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-40">
                {creating ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purge confirmation */}
      {purgeConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setPurgeConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-red-100 bg-red-50">
              <h3 className="text-lg font-semibold text-red-700">Purger la campagne</h3>
              <p className="text-sm text-red-500 mt-1">
                Supprimera TOUS les comptes, offres et interactions de <strong>{purgeConfirm.name}</strong>. Irréversible.
              </p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tapez <strong>{purgeConfirm.name}</strong> pour confirmer
              </label>
              <input type="text" value={purgeInput} onChange={(e) => setPurgeInput(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" autoFocus />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setPurgeConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">Annuler</button>
              <button onClick={handlePurge} disabled={purgeInput !== purgeConfirm.name || actionLoading === purgeConfirm.id} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-40">
                Purger définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cleanup panel */}
      {showCleanup && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCleanup(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-red-100 bg-gradient-to-r from-red-50 to-orange-50">
              <h3 className="text-lg font-semibold text-gray-900">Nettoyage progressif</h3>
              <p className="text-xs text-gray-500 mt-1">Sélectionnez les comptes seeds à supprimer pour aller vers une plateforme 100% production</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {cleanupLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-1">
                  <label className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={cleanupSelected.size === cleanupAccounts.length && cleanupAccounts.length > 0}
                      onChange={() => {
                        if (cleanupSelected.size === cleanupAccounts.length) {
                          setCleanupSelected(new Set());
                        } else {
                          setCleanupSelected(new Set(cleanupAccounts.map((a) => a.userId)));
                        }
                      }}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    Tout sélectionner ({cleanupAccounts.length})
                  </label>
                  {cleanupAccounts.map((a) => (
                    <label key={a.userId} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 cursor-pointer hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={cleanupSelected.has(a.userId)}
                        onChange={() => {
                          setCleanupSelected((prev) => {
                            const next = new Set(prev);
                            if (next.has(a.userId)) next.delete(a.userId); else next.add(a.userId);
                            return next;
                          });
                        }}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span>{a.name}</span>
                      <span className="text-[10px] font-mono text-gray-400 ml-auto">{a.seedTag}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
              <p className="text-xs text-gray-500">{cleanupSelected.size} sélectionné(s)</p>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowCleanup(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">Fermer</button>
                <button onClick={handleCleanupDelete} disabled={cleanupSelected.size === 0 || cleanupLoading} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-40">
                  Supprimer {cleanupSelected.size} comptes + traces
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CBDashboard;
